'use client'

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui'

import type { ChainId } from '../web3.types'

import { getChainName } from '../utils/chain.utils'
import { ChainIcon } from './ChainIcon'

interface ChainSelectProps<TChainId extends ChainId> {
  chains: readonly TChainId[]
  value: ChainId
  onValueChange: (chainId: TChainId) => void
  disabled?: boolean
  /** Label renderer — defaults to the full chain name. */
  chainLabel?: (chainId: ChainId) => string
}

/** Single-chain picker with icon + name, for form rows (see {@link ChainMultiSelect} for multi). */
export function ChainSelect<TChainId extends ChainId>({
  chains,
  value,
  onValueChange,
  disabled,
  chainLabel = getChainName
}: ChainSelectProps<TChainId>) {
  return (
    <Select
      value={value.toString()}
      onValueChange={next => {
        const chainId = chains.find(id => id.toString() === next)
        if (chainId !== undefined) onValueChange(chainId)
      }}
      disabled={disabled}
    >
      <SelectTrigger className="w-auto gap-2" size="sm" variant="secondary">
        <SelectValue>
          <span className="flex items-center gap-2">
            <ChainIcon chainId={value} className="size-4" />
            {chainLabel(value)}
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {chains.map(chainId => (
          <SelectItem key={chainId} value={chainId.toString()}>
            <span className="flex items-center gap-2">
              <ChainIcon chainId={chainId} className="size-4" />
              {chainLabel(chainId)}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
